import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { useServices } from "@core/servicesContext";
import { Media } from "@domain/media/media";
import { Accreditation } from "@domain/organization/accreditation";
import { Organization } from "@domain/organization/organization";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { useForm } from "@ui/components/inputs/hooks";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Stack, stack } from "@ui/components/layout";
import { EditableProfileImage } from "@ui/components/profile/editableProfileImage";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface AccreditationEditorModalProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
	initialAccreditation?: Accreditation;
	close: () => void;
}
export const AccreditationEditorModal: React.FC<AccreditationEditorModalProps> = ({
	organization,
	initialAccreditation,
	close,
	...htmlProps
}) => {
	const currentYear = new Date().getFullYear();
	const { organizationService } = useServices();
	const accreditationId = useMemo(() => initialAccreditation?.id ?? uuid(), [initialAccreditation]);

	const { fieldProps, formValue, checkSubmit } = useForm(
		{ name: initialAccreditation?.name ?? "", referenceNumber: initialAccreditation?.referenceNumber ?? "" },
		["name", "referenceNumber"],
		{
			name: (n) => n.length < 128,
			referenceNumber: (n) => n.length < 64,
		}
	);
	const [logo, setLogo] = useState<Media | undefined>(initialAccreditation?.logo);
	const [description, setDescription] = useState(initialAccreditation?.description ?? ContentState.createFromText(""));
	const [date, setDate] = useState<Date | undefined>(initialAccreditation?.date);

	const accreditationDraft: Omit<Accreditation, "logo"> = {
		id: accreditationId,
		name: formValue.name,
		referenceNumber: formValue.referenceNumber,
		description,
	};

	const { loading, run } = useAsync(
		(accreditation: Accreditation) =>
			organizationService.saveAccreditation(organization.id, accreditation, initialAccreditation?.id),
		[organization, initialAccreditation]
	);

	return (
		<Container
			close={close}
			title={
				initialAccreditation
					? organization.type === "COMPANY"
						? I18n.t("organization.distinction.edit")
						: I18n.t("organization.accreditations.edit")
					: organization.type === "COMPANY"
						? I18n.t("organization.distinction.new")
						: I18n.t("organization.accreditations.new")
			}
			keyboardEventDisabled
			{...htmlProps}
		>
			<form
				onSubmit={async (e) => {
					e.preventDefault();
					if (logo && checkSubmit()) {
						await run({ ...accreditationDraft, date, logo });
						createToast(
							I18n.t("success"),
							organization.type === "COMPANY"
								? I18n.t("organization.distinction.save.success")
								: I18n.t("organization.accreditation.save.success"),
							"success"
						);
						close();
					}
				}}
			>
				{/* Logo editor */}
				<EditableProfileImage
					type={"avatar"}
					fallbackType={organization.type}
					image={logo}
					label={I18n.t(logo ? "organization.accreditation.logo.edit" : "organization.accreditation.logo.add")}
					onUpload={(media) => setLogo(media)}
					onDelete={() => setLogo(undefined)}
					canBeEmpty
				/>
				<SimpleTextInput {...fieldProps("name")} placeholder={I18n.t("organization.accreditation.name.placeholder")} />
				<SimpleTextInput
					{...fieldProps("referenceNumber")}
					placeholder={I18n.t("organization.accreditation.reference.placeholder")}
				/>
				<DescriptionEditor
					value={description}
					onChange={(value) => setDescription(value)}
					placeholder={I18n.t("organization.accreditation.description.placeholder")}
				/>
				<Stack gap={10} justify="flex-start" align="stretch">
					<PrimaryBold>{I18n.t("organization.accreditation.date.label")}</PrimaryBold>
					<StyledDatePicker
						hideMonth
						value={date}
						onChange={(d) => setDate(d)}
						minYear={currentYear - 150}
						maxYear={currentYear}
					/>
				</Stack>
				<LoadingButton loading={loading} type="submit" disabled={!logo || !accreditationDraft.name}>
					{I18n.t(initialAccreditation ? "validate" : "create")}
				</LoadingButton>
			</form>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	> form {
		${stack(20, "flex-start", "stretch")};
		> button {
			margin: 0 auto;
		}
	}
`;

const DescriptionEditor = styled(RichTextInput)`
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		min-height: 100px;
		padding: 10px 12px 9px;
		font-size: 0.8125rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;

const StyledDatePicker = styled(DatePicker)`
	max-width: 50%;
`;
